import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CaretLeft, Strategy, Target, Megaphone, CurrencyDollar, Clock, PencilSimple, FloppyDisk, X,
  PaperPlaneRight, CheckCircle, Lightning, FlagCheckered, ClockCounterClockwise, ArrowClockwise,
  FilmSlate, Rocket, ArrowUpRight, Plus, Trash, ListChecks, CalendarBlank, NotePencil,
} from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { StatusBadge } from "@/promotions/components/StatusBadge";
import { campaignApi, videoApi } from "../useCreative";
import { fmtRelative, fmtCost } from "../creativeUtils";

const PLATFORMS = ["instagram", "tiktok", "facebook", "linkedin", "youtube", "x", "pinterest"];

// Read-only lineage the operator can see at a glance.
const WORKFLOW = [
  { key: "plan", label: "Campaign Plan", Icon: Strategy },
  { key: "project", label: "Video Studio Project", Icon: FilmSlate },
  { key: "storyboard", label: "Storyboard", Icon: ListChecks },
  { key: "scenes", label: "Scenes", Icon: FilmSlate },
  { key: "assets", label: "Assets", Icon: Rocket },
  { key: "promotion", label: "Promotion Center", Icon: Megaphone },
  { key: "delivery", label: "Delivery", Icon: PaperPlaneRight },
  { key: "published", label: "Published Content", Icon: FlagCheckered },
];

function fmtDuration(seconds) {
  if (seconds == null) return "—";
  const d = Math.round(seconds / 86400);
  if (d >= 1) return `${d} day${d === 1 ? "" : "s"}`;
  const m = Math.round(seconds / 60);
  return m >= 1 ? `${m} min` : `${seconds}s`;
}

// Compute the current workflow stage index from plan + linked project.
function currentStage(plan, project) {
  if (plan.status === "completed") return 7;
  if (plan.promotion_campaign_id) return 5;
  if (plan.video_project_id) {
    const o = project?.overview || {};
    if ((o.generated_scenes || 0) > 0) return 4;
    if ((o.total_scenes || 0) > 0) return 3;
    return 1;
  }
  return 0; // still planning
}

export default function CampaignPlan() {
  const { planId } = useParams();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [project, setProject] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    const p = await campaignApi.get(planId);
    setPlan(p);
    if (p.video_project_id) {
      try { setProject(await videoApi.getProject(p.video_project_id)); } catch { setProject(null); }
    }
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setDraft({
      title: plan.title || "",
      objective: plan.input?.objective || "",
      audience: plan.input?.audience || "",
      platforms: plan.input?.platforms || [],
      strategy: plan.strategy || "",
      content_plan: (plan.content_plan || []).map((c) => ({ ...c })),
      estimated_cost: plan.estimated_cost ?? "",
      schedule: plan.input?.schedule || "",
      constraints: plan.input?.constraints || "",
      notes: plan.input?.notes || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const patch = {
        title: draft.title,
        input: { ...(plan.input || {}), objective: draft.objective, audience: draft.audience,
                 platforms: draft.platforms, schedule: draft.schedule, constraints: draft.constraints, notes: draft.notes },
        strategy: draft.strategy,
        content_plan: draft.content_plan,
        estimated_cost: draft.estimated_cost === "" ? null : Number(draft.estimated_cost),
      };
      const updated = await campaignApi.update(planId, patch);
      setPlan(updated);
      setEditing(false);
      toast.success("Plan updated");
    } catch { toast.error("Could not save changes"); }
    setBusy(false);
  };

  const setStatus = async (status, msg) => {
    setBusy(true);
    try { setPlan(await campaignApi.setStatus(planId, status)); toast.success(msg); } catch { toast.error("Action failed"); }
    setBusy(false);
  };

  const execute = async () => {
    setBusy(true);
    try {
      const updated = await campaignApi.execute(planId);
      setPlan(updated);
      if (updated.video_project_id) setProject(await videoApi.getProject(updated.video_project_id));
      toast.success("Campaign executed — Video Studio project provisioned");
    } catch (e) { toast.error(e?.response?.data?.detail || "Execution failed"); }
    setBusy(false);
  };

  const openHistory = async () => { setShowHistory(true); setHistory(await campaignApi.history(planId)); };

  if (!plan) return <div className="p-8 text-sm text-muted-foreground" data-testid="campaign-plan-loading">Loading campaign plan…</div>;

  const executed = !!plan.video_project_id;
  const stage = currentStage(plan, project);
  const d = draft;

  return (
    <div className="space-y-6" data-testid="campaign-plan">
      <Link to="/creative/campaigns" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="campaign-back">
        <CaretLeft size={14} /> All campaigns
      </Link>

      <PromoPageHeader
        overline="AI Campaign Orchestrator"
        title={plan.title}
        description={plan.input?.objective}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={plan.status} data-testid="campaign-plan-status" />
            <button onClick={openHistory} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent" data-testid="campaign-history-btn">
              <ClockCounterClockwise size={14} /> History
            </button>
            {!editing && plan.status !== "archived" && (
              <button onClick={startEdit} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent" data-testid="campaign-edit-btn">
                <PencilSimple size={14} /> Edit
              </button>
            )}
          </div>
        }
      />

      {/* Lifecycle action bar */}
      {!editing && <LifecycleBar plan={plan} busy={busy} onStatus={setStatus} onExecute={execute} />}

      {/* Post-execution operational CTAs */}
      {executed && !editing && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="campaign-cta-bar">
          <CtaCard Icon={FilmSlate} label="Open Video Studio Project" hint={project?.title || "Provisioned project"}
            onClick={() => nav(`/creative/video/${plan.video_project_id}`)} testid="campaign-open-video" />
          <CtaCard Icon={Megaphone} label="Open Promotion Center" hint="Schedule & publish creatives"
            onClick={() => nav("/campaigns")} testid="campaign-open-promotion" />
          <CtaCard Icon={ClockCounterClockwise} label="View Execution History" hint="Full audit trail"
            onClick={openHistory} testid="campaign-open-history" />
        </div>
      )}

      {/* Read-only workflow overview */}
      <WorkflowOverview stage={stage} executed={executed} />

      {editing ? (
        <EditForm d={d} setD={setDraft} busy={busy} onCancel={() => setEditing(false)} onSave={saveEdit} />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Section title="Strategy" Icon={Strategy} testid="campaign-strategy">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{plan.strategy || "No strategy drafted yet."}</p>
            </Section>

            <Section title="Content Plan" Icon={Megaphone} testid="campaign-content-plan">
              {(plan.content_plan || []).length === 0 ? <Empty text="No content plan." /> : (
                <div className="space-y-2">
                  {plan.content_plan.map((c, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-md border border-border bg-background/40 p-3" data-testid={`campaign-content-${i}`}>
                      <span className="mt-0.5 rounded-full bg-ai/10 px-2 py-0.5 text-[10px] font-medium capitalize text-ai">{c.platform}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">{c.theme}</div>
                        {c.format && <div className="text-xs text-muted-foreground">{c.format}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Storyboard Plan" Icon={FilmSlate} testid="campaign-storyboard"
              aside={executed && <span className="text-xs text-muted-foreground">Edit scenes in Video Studio →</span>}>
              {(plan.storyboard_plan || []).length === 0 ? <Empty text="No storyboard yet." /> : (
                <div className="space-y-2">
                  {plan.storyboard_plan.map((s, i) => (
                    <div key={i} className="rounded-md border border-border bg-background/40 p-3" data-testid={`campaign-scene-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">#{i + 1}</span>
                        <span className="text-sm font-medium">{s.title}</span>
                        <span className="rounded-full border border-border px-2 py-0.5 text-[10px] capitalize text-muted-foreground">{s.kind}</span>
                        {s.duration_seconds ? <span className="text-[10px] text-muted-foreground">{s.duration_seconds}s</span> : null}
                      </div>
                      {s.structured?.subject && <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{s.structured.subject}</p>}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Publishing Schedule" Icon={CalendarBlank} testid="campaign-schedule-section">
              {(plan.publishing_schedule || []).length === 0 ? <Empty text="No schedule." /> : (
                <div className="space-y-2">
                  {plan.publishing_schedule.map((s, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 rounded-md border border-border bg-background/40 p-3 text-xs" data-testid={`campaign-schedule-${i}`}>
                      <span className="rounded-full bg-ai/10 px-2 py-0.5 font-medium capitalize text-ai">{s.platform}</span>
                      <div className="min-w-0 flex-1"><div className="font-medium text-foreground">{s.when}</div><div className="text-muted-foreground">{s.cadence}</div></div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Section title="Overview" Icon={Target} testid="campaign-overview">
              <dl className="space-y-3 text-sm">
                <Meta Icon={Target} label="Audience" value={plan.input?.audience || "—"} />
                <Meta Icon={Megaphone} label="Platforms" value={(plan.input?.platforms || []).join(", ") || "—"} />
                <Meta Icon={CurrencyDollar} label="Est. budget" value={plan.estimated_cost != null ? fmtCost(plan.estimated_cost) : "—"} />
                <Meta Icon={Clock} label="Est. duration" value={fmtDuration(plan.estimated_duration)} />
                <Meta Icon={CalendarBlank} label="Schedule" value={plan.input?.schedule || "—"} />
                <Meta Icon={ArrowClockwise} label="Modified" value={fmtRelative(plan.updated_at)} />
              </dl>
            </Section>

            {plan.input?.constraints && (
              <Section title="Constraints" Icon={ListChecks} testid="campaign-constraints">
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{plan.input.constraints}</p>
              </Section>
            )}
            {plan.input?.notes && (
              <Section title="Notes" Icon={NotePencil} testid="campaign-notes">
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{plan.input.notes}</p>
              </Section>
            )}
          </div>
        </div>
      )}

      {showHistory && <HistoryDrawer history={history} onClose={() => setShowHistory(false)} />}
    </div>
  );
}

// ---- Lifecycle action bar ---------------------------------------------------
function LifecycleBar({ plan, busy, onStatus, onExecute }) {
  const s = plan.status;
  const actions = [];
  if (s === "draft") actions.push({ label: "Submit for Review", Icon: PaperPlaneRight, onClick: () => onStatus("ready_for_review", "Sent for review"), testid: "campaign-submit-review", variant: "outline" });
  if (s === "ready_for_review") actions.push({ label: "Approve", Icon: CheckCircle, onClick: () => onStatus("approved", "Plan approved"), testid: "campaign-approve", variant: "outline" });
  if (["draft", "ready_for_review", "approved"].includes(s)) actions.push({ label: "Execute Campaign", Icon: Lightning, onClick: onExecute, testid: "campaign-execute", variant: "primary" });
  if (["executed", "in_progress"].includes(s)) {
    actions.push({ label: "Re-provision", Icon: ArrowClockwise, onClick: onExecute, testid: "campaign-reexecute", variant: "outline" });
    actions.push({ label: "Mark Completed", Icon: FlagCheckered, onClick: () => onStatus("completed", "Campaign completed"), testid: "campaign-complete", variant: "primary" });
  }
  if (actions.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-4" data-testid="campaign-lifecycle-bar">
      <span className="promo-overline mr-2">Lifecycle</span>
      {actions.map((a) => (
        <button key={a.testid} onClick={a.onClick} disabled={busy} data-testid={a.testid}
          className={`flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60 ${a.variant === "primary" ? "bg-ai text-ai-foreground hover:opacity-90" : "border border-border hover:bg-accent"}`}>
          <a.Icon size={15} weight={a.variant === "primary" ? "fill" : "regular"} /> {a.label}
        </button>
      ))}
    </div>
  );
}

// ---- Workflow overview ------------------------------------------------------
function WorkflowOverview({ stage, executed }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="campaign-workflow">
      <div className="mb-3 flex items-center justify-between">
        <span className="promo-overline">Campaign Lifecycle</span>
        <span className="text-xs text-muted-foreground">{executed ? "Coordinating downstream modules" : "Not yet executed"}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {WORKFLOW.map((w, i) => {
          const done = i < stage;
          const active = i === stage;
          return (
            <div key={w.key} className="flex items-center gap-1.5">
              <div data-testid={`campaign-stage-${w.key}`}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${active ? "border-ai bg-ai/10 text-ai" : done ? "border-success/30 bg-success/10 text-success" : "border-border text-muted-foreground"}`}>
                <w.Icon size={13} weight={active ? "fill" : "regular"} /> {w.label}
              </div>
              {i < WORKFLOW.length - 1 && <span className={`text-xs ${i < stage ? "text-success" : "text-muted-foreground/40"}`}>→</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Edit form (light strategic editing only) -------------------------------
function EditForm({ d, setD, busy, onCancel, onSave }) {
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const togglePlatform = (p) => setD((s) => ({ ...s, platforms: s.platforms.includes(p) ? s.platforms.filter((x) => x !== p) : [...s.platforms, p] }));
  const setContent = (i, k, v) => setD((s) => ({ ...s, content_plan: s.content_plan.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const addContent = () => setD((s) => ({ ...s, content_plan: [...s.content_plan, { platform: s.platforms[0] || "instagram", theme: "", format: "" }] }));
  const delContent = (i) => setD((s) => ({ ...s, content_plan: s.content_plan.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-6" data-testid="campaign-edit-form">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Fld label="Campaign Title"><input value={d.title} onChange={(e) => set("title", e.target.value)} className="fld" data-testid="campaign-edit-title" /></Fld>
        <Fld label="Target Audience"><input value={d.audience} onChange={(e) => set("audience", e.target.value)} className="fld" data-testid="campaign-edit-audience" /></Fld>
      </div>
      <Fld label="Objective"><textarea rows={2} value={d.objective} onChange={(e) => set("objective", e.target.value)} className="fld" data-testid="campaign-edit-objective" /></Fld>
      <Fld label="Platforms">
        <div className="flex flex-wrap gap-1.5">
          {PLATFORMS.map((p) => (
            <button key={p} type="button" onClick={() => togglePlatform(p)} className={`rounded-full border px-2.5 py-1 text-xs capitalize ${d.platforms.includes(p) ? "border-ai bg-ai/10 text-ai" : "border-border text-muted-foreground hover:bg-accent"}`} data-testid={`campaign-edit-platform-${p}`}>{p}</button>
          ))}
        </div>
      </Fld>
      <Fld label="Strategy"><textarea rows={5} value={d.strategy} onChange={(e) => set("strategy", e.target.value)} className="fld" data-testid="campaign-edit-strategy" /></Fld>

      <Fld label="Content Plan">
        <div className="space-y-2">
          {d.content_plan.map((c, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`campaign-edit-content-${i}`}>
              <input value={c.platform} onChange={(e) => setContent(i, "platform", e.target.value)} placeholder="platform" className="fld w-28 shrink-0" />
              <input value={c.theme} onChange={(e) => setContent(i, "theme", e.target.value)} placeholder="theme" className="fld flex-1" />
              <input value={c.format} onChange={(e) => setContent(i, "format", e.target.value)} placeholder="format" className="fld w-40 shrink-0" />
              <button onClick={() => delContent(i)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
            </div>
          ))}
          <button onClick={addContent} className="flex items-center gap-1.5 rounded-md border border-dashed border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent" data-testid="campaign-edit-content-add"><Plus size={13} /> Add content row</button>
        </div>
      </Fld>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Fld label="Estimated Budget ($)"><input type="number" value={d.estimated_cost} onChange={(e) => set("estimated_cost", e.target.value)} className="fld" data-testid="campaign-edit-budget" /></Fld>
        <Fld label="Schedule"><input value={d.schedule} onChange={(e) => set("schedule", e.target.value)} className="fld" data-testid="campaign-edit-schedule" /></Fld>
      </div>
      <Fld label="Constraints"><textarea rows={2} value={d.constraints} onChange={(e) => set("constraints", e.target.value)} className="fld" data-testid="campaign-edit-constraints" /></Fld>
      <Fld label="Notes"><textarea rows={2} value={d.notes} onChange={(e) => set("notes", e.target.value)} className="fld" data-testid="campaign-edit-notes" /></Fld>

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent" data-testid="campaign-edit-cancel">Cancel</button>
        <button onClick={onSave} disabled={busy} className="flex items-center gap-1.5 rounded-md bg-ai px-5 py-2 text-sm font-medium text-ai-foreground hover:opacity-90 disabled:opacity-60" data-testid="campaign-edit-save">
          {busy ? <ArrowClockwise size={15} className="animate-spin" /> : <FloppyDisk size={15} />} Save changes
        </button>
      </div>
    </div>
  );
}

// ---- Small presentational helpers ------------------------------------------
function Section({ title, Icon, children, testid, aside }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5" data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold"><Icon size={15} className="text-ai" /> {title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}
function Meta({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0"><dt className="text-xs text-muted-foreground">{label}</dt><dd className="truncate text-sm font-medium capitalize">{value}</dd></div>
    </div>
  );
}
function Empty({ text }) { return <p className="text-sm text-muted-foreground">{text}</p>; }
function Fld({ label, children }) { return <div><label className="promo-overline mb-1 block">{label}</label>{children}</div>; }
function CtaCard({ Icon, label, hint, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-ai/50 hover:bg-accent/40">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-ai/15 text-ai"><Icon size={18} weight="fill" /></div>
      <div className="min-w-0 flex-1"><div className="flex items-center gap-1 text-sm font-medium">{label} <ArrowUpRight size={13} className="opacity-0 transition-opacity group-hover:opacity-100" /></div><div className="truncate text-xs text-muted-foreground">{hint}</div></div>
    </button>
  );
}

function HistoryDrawer({ history, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="campaign-history-drawer">
      <div className="h-full w-full max-w-md overflow-auto border-l border-border bg-card p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">Execution History</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        {history.length === 0 ? <p className="text-sm text-muted-foreground">No history yet.</p> : (
          <ol className="relative space-y-4 border-l border-border pl-5">
            {history.map((h) => (
              <li key={h.id} className="relative" data-testid={`campaign-history-${h.id}`}>
                <span className="absolute -left-[23px] top-1 h-2.5 w-2.5 rounded-full bg-ai" />
                <div className="text-sm font-medium capitalize">{String(h.action).replace(/_/g, " ")}</div>
                <div className="text-xs text-muted-foreground">{fmtRelative(h.at)}{h.actor ? ` · ${h.actor}` : ""}</div>
                {h.detail && Object.keys(h.detail).length > 0 && (
                  <pre className="mt-1 overflow-auto rounded bg-muted/50 p-2 text-[10px] text-muted-foreground">{JSON.stringify(h.detail, null, 2)}</pre>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
